import { useEffect, useState, useCallback } from 'react'
import { Grid3x3, RefreshCw, Code } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface TechCoverage {
  sigma: number
  yara: number
  total: number
  level: 'cold' | 'warm' | 'hot'
  rule_names: string[]
}

interface CoverageData {
  by_technique: Record<string, TechCoverage>
  tactics: { id: string; name: string }[]
  summary: {
    techniques_with_coverage: number
    total_sigma_rules: number
    total_yara_rules: number
    hot_techniques: number
    warm_techniques: number
  }
}

const LEVEL_TONE: Record<string, string> = {
  cold: 'bg-surface-raised text-text-muted border-border',
  warm: 'bg-amber/15 text-amber-light border-amber/30',
  hot:  'bg-emerald/15 text-emerald border-emerald/30',
}

export default function DetectionCoveragePage() {
  const [data, setData] = useState<CoverageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sec/detection-coverage')
      setData(r.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Sort techniques by ID across all data — group/display by total descending
  const sortedTechs = data
    ? Object.entries(data.by_technique).sort((a, b) => b[1].total - a[1].total)
    : []

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Grid3x3 size={24} className="text-emerald" /> Detection Coverage Map
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Sigma + YARA rules cross-tabulated against ATT&CK techniques. Cold = no rules · warm = 1-4 · hot = 5+.
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-ghost text-xs">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Techniques covered" value={data.summary.techniques_with_coverage} tone="emerald" />
          <Tile label="Hot (5+ rules)"    value={data.summary.hot_techniques} tone="emerald" />
          <Tile label="Warm (1-4)"         value={data.summary.warm_techniques} tone="amber" />
          <Tile label="Sigma rules"        value={data.summary.total_sigma_rules} tone="blue" />
          <Tile label="YARA rules"         value={data.summary.total_yara_rules} tone="blue" />
        </div>
      )}

      {/* Heat grid — flat list of techniques with coverage, sorted by intensity */}
      <div className="card p-3">
        <h3 className="text-xs font-semibold text-text-primary mb-2">Techniques by detection density</h3>
        {sortedTechs.length === 0 ? (
          <p className="text-[11px] text-text-muted italic text-center py-4">
            No coverage yet — Sigma + YARA rule caches may be empty. Run the refresh tasks.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
            {sortedTechs.map(([tech, cov]) => (
              <button key={tech} type="button"
                onClick={() => setSelected(tech === selected ? null : tech)}
                className={clsx('rounded p-1.5 border text-left transition-all',
                  LEVEL_TONE[cov.level],
                  tech === selected && 'ring-2 ring-blue/60')}>
                <div className="font-mono text-[10px] font-bold">{tech}</div>
                <div className="text-[9px] mt-0.5 opacity-75">
                  S:{cov.sigma} · Y:{cov.yara}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected technique detail */}
      {selected && data?.by_technique[selected] && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center gap-2 mb-2">
            <Code size={12} className="text-blue-light" />
            <h3 className="text-sm font-semibold text-text-primary">{selected}</h3>
            <span className={clsx('badge text-[9px] uppercase', LEVEL_TONE[data.by_technique[selected].level])}>
              {data.by_technique[selected].level}
            </span>
          </div>
          <div className="text-[11px] text-text-secondary mb-2">
            {data.by_technique[selected].sigma} Sigma + {data.by_technique[selected].yara} YARA rules cover this technique.
          </div>
          {data.by_technique[selected].rule_names.length > 0 && (
            <ul className="text-[10px] font-mono text-text-muted space-y-0.5">
              {data.by_technique[selected].rule_names.map((n, i) => <li key={i}>· {n}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  const t = tone === 'emerald' ? 'text-emerald' : tone === 'amber' ? 'text-amber-light' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t)}>{value}</div>
    </div>
  )
}
